// RTK Query slice for Personal Access Tokens (PATs)
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";

export const personalTokensApi = createApi({
  reducerPath: "personalTokensApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  keepUnusedDataFor: 300, // Cache for 5 minutes
  tagTypes: ["PersonalTokens", "TokenHandoff"],
  endpoints: (builder) => ({
    /** Get all active personal tokens */
    getPersonalTokens: builder.query({
      query: () => ({
        url: "api/pats",
        method: "GET",
      }),
      transformResponse: (response) => {
        // Filter out revoked tokens
        const tokens = Array.isArray(response) ? response : [];
        return tokens.filter((t) => !t.revokedAt);
      },
      providesTags: ["PersonalTokens"],
    }),

    /** Create new personal access token */
    createPersonalToken: builder.mutation({
      query: (body) => ({
        url: "api/pats",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PersonalTokens"],
    }),

    /** Revoke (delete) personal access token */
    revokePersonalToken: builder.mutation({
      query: (publicId) => ({
        url: `api/pats/${publicId}`,
        method: "DELETE",
      }),
      // Optimistic update: remove from cache immediately
      async onQueryStarted(publicId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          personalTokensApi.util.updateQueryData(
            "getPersonalTokens",
            undefined,
            (draft) => {
              return draft.filter((t) => t.publicId !== publicId);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["PersonalTokens"],
    }),

    /** Start JWT mobile handoff (generate QR code) */
    startJwtHandoff: builder.mutation({
      query: (body) => ({
        url: "api/auth/jwt-mobile/start",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TokenHandoff"],
    }),
  }),
});

export const {
  useGetPersonalTokensQuery,
  useCreatePersonalTokenMutation,
  useRevokePersonalTokenMutation,
  useStartJwtHandoffMutation,
} = personalTokensApi;
